import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Status } from "lib/enums";
import { formatMainDataDates } from "lib/helpers";
import { SiteDesignType } from "lib/types";
import { RootState } from "reducers/store";
import {
    SiteDesignsGetPayload, getSiteDesigns, postSiteDesign, SiteDesignInsertPayload, putSiteDesign,
    SiteDesignDeletePayload, deleteSiteDesign, SiteDesignUpdatePayload, duplicateSiteDesign,
    saveChangesSiteDesign, SiteDesignSaveChangesPayload, exportSiteDesignThunk
} from "./siteDesignActions";

type SiteDesignState = {
    data: { [projectId: number]: { [siteId: number]: SiteDesignType[] } },
    statuses: { [projectId: number]: { [siteId: number]: Status } },
    SiteDesignStatus: string
};

const initialState: SiteDesignState = {
    data: {},
    statuses: {},
    SiteDesignStatus: Status.Idle,
};

const siteDesignSlice = createSlice({
    name: 'siteDesign',
    initialState: initialState,
    reducers: {},
    extraReducers: builder => builder
        .addCase(getSiteDesigns.pending, (state: SiteDesignState, action) => {
            const { projectId, siteId } = action.meta.arg || {};
            if (projectId !== undefined && siteId !== undefined) {
                state.statuses[projectId] = {
                    ...state.statuses[projectId],
                    [siteId]: Status.Pending
                };
            }
        })
        .addCase(getSiteDesigns.fulfilled, (state: SiteDesignState, action: PayloadAction<SiteDesignsGetPayload>) => {
            const { projectId, siteId, data } = action.payload;
            if (projectId !== undefined && siteId !== undefined && data) {
                state.data[projectId] = {
                    ...state.data[projectId],
                    [siteId]: formatMainDataDates(data.sort((a, b) => b.id - a.id))
                };

                state.statuses[projectId] = {
                    ...state.statuses[projectId],
                    [siteId]: Status.Success
                };
            }
        })
        .addCase(getSiteDesigns.rejected, (state: SiteDesignState, action) => {
            const { projectId, siteId } = action.meta.arg || {};
            if (projectId !== undefined && siteId !== undefined) {
                state.statuses[projectId] = {
                    ...state.statuses[projectId],
                    [siteId]: Status.Rejected
                };
            }
            console.log(action.error);
        })
        .addCase(postSiteDesign.pending, state => {
            state.SiteDesignStatus = Status.Pending;
        })
        .addCase(postSiteDesign.fulfilled, (state, action: PayloadAction<SiteDesignInsertPayload>) => {
            const { projectId, siteId, data } = action.payload;
            if (projectId !== undefined && siteId !== undefined && data) {
                state.data[projectId] = {
                    ...state.data[projectId],
                    [siteId]: formatMainDataDates([
                        ...state.data[projectId][siteId],
                        data
                    ]).sort((a, b) => b.id - a.id)
                };
                state.SiteDesignStatus = Status.Success;
            }
        })
        .addCase(putSiteDesign.pending, state => {
            state.SiteDesignStatus = Status.Pending;
        })
        .addCase(putSiteDesign.fulfilled, (state, action: PayloadAction<SiteDesignUpdatePayload>) => {
            const { projectId, siteId, data } = action.payload;
            if (projectId !== undefined && siteId !== undefined && data) {
                const siteDesignIndex = state.data[projectId][siteId].findIndex(siteDesign => siteDesign.id === data.id);
                const newState = [...state.data[projectId][siteId]];
                newState[siteDesignIndex] = data;

                state.data[projectId] = {
                    ...state.data[projectId],
                    [siteId]: formatMainDataDates(newState.sort((a, b) => b.id - a.id))
                };
                state.SiteDesignStatus = Status.Success;
            }
        })
        .addCase(deleteSiteDesign.rejected, (state, action) => {
            state.SiteDesignStatus = Status.Rejected
        })
        .addCase(deleteSiteDesign.fulfilled, (state, action: PayloadAction<SiteDesignDeletePayload>) => {
            const { projectId, siteId, designId } = action.payload;
            if (projectId !== undefined && siteId !== undefined && designId !== undefined) {
                const newState = state.data[projectId][siteId].filter(siteDesign => siteDesign.id !== designId);
                state.data[projectId][siteId] = newState;
            }
        })
        .addCase(duplicateSiteDesign.pending, state => {
            state.SiteDesignStatus = Status.Pending;
        })
        .addCase(duplicateSiteDesign.fulfilled, (state, action: PayloadAction<SiteDesignInsertPayload>) => {
            const { projectId, siteId, data } = action.payload;
            if (projectId !== undefined && siteId !== undefined && data) {
                const newState = formatMainDataDates([
                    ...state.data[projectId][siteId],
                    data
                ]).sort((a, b) => b.id - a.id);

                state.data[projectId] = {
                    ...state.data[projectId],
                    [siteId]: newState
                };
                state.SiteDesignStatus = Status.Success;
            }
        })
        .addCase(duplicateSiteDesign.rejected, state => {
            state.SiteDesignStatus = Status.Rejected;
        })
        .addCase(saveChangesSiteDesign.pending, state => {
            state.SiteDesignStatus = Status.Pending;
        })
        .addCase(saveChangesSiteDesign.fulfilled, (state, action: PayloadAction<SiteDesignSaveChangesPayload>) => {
            state.SiteDesignStatus = Status.Success;
        })
        .addCase(saveChangesSiteDesign.rejected, (state, action) => {
            state.SiteDesignStatus = Status.Rejected;
        })
        .addCase(exportSiteDesignThunk.pending, state => {
            state.SiteDesignStatus = Status.Pending;
        })
        .addCase(exportSiteDesignThunk.fulfilled, state => {
            state.SiteDesignStatus = Status.Success;
        })
        .addCase(exportSiteDesignThunk.rejected, state => {
            state.SiteDesignStatus = Status.Rejected;
        })
});

export const selectSiteDesigns = (projectId: number, siteId: number) => (state: RootState): SiteDesignType[] => {
    const sites = state.siteDesign.data[projectId];
    if (sites == null) {
        return [];
    } else {
        return sites[siteId] ?? [];
    }
}

export const selectSiteDesignStatus = (projectId: number, siteId: number) => (state: RootState): Status => {
    const siteStatuses = state.siteDesign.statuses[projectId];
    if (siteStatuses == null) {
        return Status.Idle;
    } else {
        return siteStatuses[siteId] ?? Status.Idle;
    }
}

export const selectAreSiteDesignsLoading = (state: RootState): boolean => Object.values(state.siteDesign.statuses)
    .flatMap(statuses => Object.values(statuses))
    .some(status => status === Status.Pending) || state.siteDesign.SiteDesignStatus === Status.Pending

export default siteDesignSlice.reducer;